import React from "react";
import { useParams } from "react-router-dom";

import NotFound from "../components/Errors/NotFound";
import Forbidden from "../components/Errors/Forbidden";
import Unknown from "../components/Errors/Unknown";
import { useAccessRightsQuery } from "../hooks";
import { accessRightsShape } from "../shapes";

export function enforceAccessRightCanRead(accessRights: accessRightsShape) {
  return accessRights.canRead;
}

export function enforceAccessRightCanUpdate(accessRights: accessRightsShape) {
  return accessRights.canUpdate;
}

type WithAccessRightsProps = {
  testAccessRights?: accessRightsShape;
  accessRights: accessRightsShape;
};

const withAccessRights = <
  T extends WithAccessRightsProps = WithAccessRightsProps,
>(
  ChildComponent: React.ComponentType<T>,
  type:
    | "APISubscription"
    | "Asset"
    | "Batch"
    | "Customer"
    | "EDM::EdgeDevice"
    | "Event"
    | "Instrumentation"
    | "Node"
    | "Nodes"
    | "Product"
    | "Recipe"
    | "System"
    | "Tenant"
    | "Usergroup"
    | "Document",
  validate?: $TSFixMeFunction,
) => {
  function WrappedComponent(props: Readonly<Omit<T, "accessRights">>) {
    const params = useParams<{ id?: string; objects?: $TSFixMe }>();
    const { testAccessRights } = props;
    // FIXME: Hack to make all components work that are
    // using match from useAccessRights instead of withRouter
    const match = { params: { ...params } };

    const getPermitableType = () => {
      if (type !== undefined) return type;
      const { objects } = params;
      if (objects !== undefined)
        return objects.substring(0, objects.length - 1);
      return undefined;
    };
    const neededType = getPermitableType();

    const { data, isError } = useAccessRightsQuery(neededType, {
      enabled: !testAccessRights && !!match.params.id,
    });

    const accessRights = testAccessRights ?? (data as accessRightsShape);

    if (!params.id) return <ChildComponent {...(props as T)} match={match} />;
    if (isError) return <Unknown />;
    if (!accessRights) return null;
    if (!validate || validate(accessRights))
      return (
        <ChildComponent
          {...(props as T)}
          match={match}
          accessRights={accessRights}
        />
      );
    if (accessRights.canRead) return <Forbidden />;
    return <NotFound />;
  }

  WrappedComponent.displayName =
    ChildComponent.displayName ?? ChildComponent.name;

  return WrappedComponent;
};

export default withAccessRights;
